import React from 'react'
import { useTranslation } from 'language/translator'

import ImageSection from './ImageSection'
import TextSection from './TextSection'

import {
  InnerWidgetPatchBody,
  WidgetType,
  TViewport,
  TWidget,
  isWidgetSettingsLoaded,
  isCoreWidget
} from 'types/GlobalWidget'
import {
  WidgetTagPatchAttributes,
  WidgetTagPostAttributes,
  WidgetTagStatusPatchAttributes,
  WidgetTagStatusPostAttributes
} from 'types/GlobalWidgetTag'

import { useCommonAttributeOptions } from 'redux/hooks/AttributeOptions'

import TitleSection from './TitleSection'
import VisualizationSection from './VisualizationSection'
import DatasetsSection from './DatasetsSection'
import SegmentSection from './SegmentSection'
import DatesSection from './DatesSection'
import FilterSection from './FilterSection'
import AreaSection from './AreaSection'
import ViewportSection from './ViewportSection'
import ReOrderSection from './ReOrderSection'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import { TPutFunnelStages } from 'redux/api/Widgets/types'
import { useAdditionalSegmentActions } from 'redux/actions/AdditionalSegments'
import { useVariantOptionActions } from 'redux/actions/VariantOptions'
import {
  ComparativeDisplayType,
  RollingType,
  UnitPrefix
} from 'types/GlobalKpiOption'
import { ShowPerEnum } from 'types/GlobalMetric'
import { useKpiOptionActions } from 'redux/actions/KpiOptions'
import { useWidgetTagActions } from 'redux/actions/WidgetTags'
import { useWidgetTagStatusActions } from 'redux/actions/WidgetTagStatuses'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'
import { TPatchKpiOptionBody, TPostKpiOptionBody } from 'redux/api/KpiOptions'

interface WidgetSettingsProps {
  onClose: () => void
  open: boolean
  updateWidget: (body: InnerWidgetPatchBody) => void
  widget: TWidget

  // NOT REQUIRED PROPS
  image?: {
    url: string
    name: string
  } | null
  setViewport?: (value: TViewport) => void
  viewport?: TViewport
}

const WidgetSettings = (props: WidgetSettingsProps) => {
  const {
    image,
    onClose,
    open,
    setViewport = () => console.warn('function not supported for Export Table'),
    updateWidget,
    widget,
    viewport
  } = props

  const { t } = useTranslation()

  const filterAttributeOptions = useCommonAttributeOptions(
    widget,
    'filter_attribute'
  )

  const {
    tryCreateAdditionalSegmentBy,
    tryUpdateAdditionalSegmentBy,
    tryDeleteAdditionalSegmentBy
  } = useAdditionalSegmentActions()

  const {
    tryCreateVariantOptionBy,
    tryUpdateVariantOptionBy,
    tryDeleteVariantOptionBy
  } = useVariantOptionActions()

  const { tryCreateKpiOption, tryUpdateKpiOption, tryDeleteKpiOption } =
    useKpiOptionActions()

  const { tryCreateWidgetTag, tryUpdateWidgetTag, tryDeleteWidgetTag } =
    useWidgetTagActions()

  const {
    tryCreateWidgetTagStatus,
    tryUpdateWidgetTagStatus,
    tryDeleteWidgetTagStatus
  } = useWidgetTagStatusActions()

  const { tryPutFunnelStages, tryUploadWidgetImage } =
    useDashboardWidgetActions()

  /** UPLOAD IMAGE */
  const uploadImage = (image: Blob, name: string) => {
    if (isCoreWidget(widget)) {
      tryUploadWidgetImage(widget.id, image, name, null)
    } else {
      console.warn('unsupported function for widget class')
    }
  }

  if (isCoreWidget(widget) && widget.type === WidgetType.IMAGE) {
    return (
      <SettingsDrawer
        backLabel={t('global', 'close')}
        onBack={onClose}
        onClose={onClose}
        open={open}
        title={t('global', 'image')}
      >
        <ImageSection
          image={image}
          updateWidget={updateWidget}
          uploadImage={uploadImage}
        />
      </SettingsDrawer>
    )
  }

  if (isCoreWidget(widget) && widget.type === WidgetType.TEXT) {
    return (
      <SettingsDrawer
        backLabel={t('global', 'close')}
        onBack={onClose}
        onClose={onClose}
        open={open}
        title={t('global', 'text')}
      >
        <TextSection
          textAlign={widget.text_align}
          textVerticalAlign={widget.text_vertical_align}
          updateWidget={updateWidget}
        />
      </SettingsDrawer>
    )
  }

  if (!isWidgetSettingsLoaded(widget)) return null

  /** CREATE, UPDATE, DELETE ADDITIONAL SEGMENTS */
  const createAdditionalSegmentBy = (attributeOptionId: string) => {
    tryCreateAdditionalSegmentBy({
      widget_id: widget.id,
      attribute_option_id: attributeOptionId
    })
  }

  const updateAdditionalSegmentBy = (
    segmentId: string,
    attributeOptionId: string
  ) => {
    tryUpdateAdditionalSegmentBy(segmentId, {
      attribute_option_id: attributeOptionId
    })
  }

  const deleteAdditionalSegmentBy = (segmentId: string) => {
    tryDeleteAdditionalSegmentBy(segmentId, widget.id)
  }

  /** CREATE, UPDATE, DELETE VARIANT COLUMNS */
  const createVariantOptionBy = (
    variantKpiTemplateId: string,
    title: string
  ) => {
    tryCreateVariantOptionBy({
      widget_id: widget.id,
      variant_kpi_template_id: variantKpiTemplateId,
      title
    })
  }

  const updateVariantOptionBy = (
    variantOptionId: string,
    body: {
      active_show_per?: ShowPerEnum | null
      unit_prefix?: UnitPrefix | null
      title?: string
      number_of_decimals?: number
      rolling_active?: RollingType | null
      comparative_display_type?: ComparativeDisplayType
    }
  ) => {
    tryUpdateVariantOptionBy(variantOptionId, body)
  }

  const deleteVariantOptionBy = (variantOptionId: string) => {
    tryDeleteVariantOptionBy(variantOptionId, widget.id)
  }


  /** CREATE, UPDATE, DELETE KPI OPTIONS */
  const createKpiOption = (
    body: TPostKpiOptionBody,
    callback?: (err?: boolean) => void
  ) => {
    tryCreateKpiOption(body, callback)
  }

  const updateKpiOption = (
    kpiId: string,
    body: TPatchKpiOptionBody,
    cb: () => void
  ) => {
    tryUpdateKpiOption(kpiId, body, cb)
  }

  const deleteKpiOption = (kpiId: string) => {
    tryDeleteKpiOption(kpiId, widget.id)
  }

  /** CREATE, UPDATE, DELETE TAG OPTIONS */
  const createWidgetTag = (body: WidgetTagPostAttributes) => {
    tryCreateWidgetTag(body, widget.id)
  }

  const updateWidgetTag = (tagId: string, body: WidgetTagPatchAttributes) => {
    tryUpdateWidgetTag(tagId, body, widget.id)
  }

  const deleteWidgetTag = (tagId: string) => {
    tryDeleteWidgetTag(tagId, widget.id)
  }

  /** CREATE, UPDATE, DELETE TAG OPTION STATUS */
  const createWidgetTagStatus = (
    body: WidgetTagStatusPostAttributes,
    tagId: string
  ) => {
    tryCreateWidgetTagStatus(body, widget.id, tagId)
  }

  const updateWidgetTagStatus = (
    tagStatusId: string,
    body: WidgetTagStatusPatchAttributes,
    tagId: string
  ) => {
    tryUpdateWidgetTagStatus(tagStatusId, body, widget.id, tagId)
  }

  const deleteWidgetTagStatus = (tagStatusId: string, tagId: string) => {
    tryDeleteWidgetTagStatus(tagStatusId, widget.id, tagId)
  }

  /** PUT IMAGE */
  const putFunnelStages = (data: TPutFunnelStages) => {
    if (isCoreWidget(widget)) {
      tryPutFunnelStages(widget.id, data)
    } else {
      console.warn('unsupported function for widget class')
    }
  }

  return (
    <SettingsDrawer
      backLabel={t('global', 'close')}
      onBack={onClose}
      onClose={onClose}
      open={open}
      title={widget.settings.title}
    >
      {isCoreWidget(widget) && (
        <TitleSection
          onClose={onClose}
          settings={widget.settings}
          updateWidget={updateWidget}
        />
      )}
      {isCoreWidget(widget) && (
        <VisualizationSection
          settings={widget.settings}
          updateWidget={updateWidget}
        />
      )}
      <DatasetsSection
        createKpiOption={createKpiOption}
        createWidgetTag={createWidgetTag}
        createWidgetTagStatus={createWidgetTagStatus}
        deleteKpiOption={deleteKpiOption}
        deleteWidgetTag={deleteWidgetTag}
        deleteWidgetTagStatus={deleteWidgetTagStatus}
        id={widget.id}
        onClose={onClose}
        widget={widget}
        updateKpiOption={updateKpiOption}
        updateWidget={updateWidget}
        updateWidgetTag={updateWidgetTag}
        updateWidgetTagStatus={updateWidgetTagStatus}
      />
      <SegmentSection
        createAdditionalSegmentBy={createAdditionalSegmentBy}
        createVariantOptionBy={createVariantOptionBy}
        deleteAdditionalSegmentBy={deleteAdditionalSegmentBy}
        deleteVariantOptionBy={deleteVariantOptionBy}
        onClose={onClose}
        widget={widget}
        updateAdditionalSegmentBy={updateAdditionalSegmentBy}
        updateVariantOptionBy={updateVariantOptionBy}
        updateWidget={updateWidget}
      />
      <DatesSection
        widgetClass={widget.class}
        onClose={onClose}
        settings={widget.settings}
        updateWidget={updateWidget}
      />
      <FilterSection
        filterAttributeOptions={filterAttributeOptions}
        filters={widget.settings.filters}
        id={widget.id}
        onClose={onClose}
        settings={widget.settings}
        updateFilters={(filters) => updateWidget({ filters: filters })}
      />
      <ReOrderSection
        putFunnelStages={putFunnelStages}
        settings={widget.settings}
      />
      <AreaSection settings={widget.settings} updateWidget={updateWidget} />
      <ViewportSection
        settings={widget.settings}
        setViewport={setViewport}
        updateWidget={updateWidget}
        viewport={viewport || widget.settings.viewport}
      />
    </SettingsDrawer>
  )
}

export default WidgetSettings
